/* ------------------------------------------------------------------
   Cart state, drawer, shipping progress, bundle counter.
   No framework. Cart is the single source of truth; every mutation
   refetches /cart.js and re-renders subscribers.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var cfg = window.theme || {};
  var listeners = [];
  var state = { cart: null };

  function money(cents) {
    var currency = (state.cart && state.cart.currency) || cfg.currency || 'INR';

    // Lakh/crore grouping and a rupee format string are both wrong for any
    // other currency, which a Markets storefront will hand us.
    if (currency !== 'INR') {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(cents / 100);
      } catch (e) { /* unknown code - fall through to the shop format */ }
    }

    var fmt = cfg.moneyFormat || '\u20B9{{amount}}';
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    // Indian grouping: 1,23,456
    var parts = amount.split('.');
    var n = parts[0];
    var last3 = n.slice(-3);
    var rest = n.slice(0, -3);
    if (rest) last3 = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    var out = last3 + (parts[1] ? '.' + parts[1] : '');
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, out);
  }

  function subscribe(fn) { listeners.push(fn); if (state.cart) fn(state.cart); }
  function publish() { listeners.forEach(function (fn) { fn(state.cart); }); }

  // Locale-prefixed storefronts serve the cart under /fr/cart.js, not /cart.js.
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fetchCart() {
    return fetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { state.cart = cart; publish(); return cart; });
  }

  function addItem(id, quantity) {
    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: quantity || 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add that item.'); });
      return r.json();
    }).then(function () { return fetchCart(); });
  }

  function changeItem(key, quantity) {
    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); })
      .then(function (cart) { state.cart = cart; publish(); return cart; });
  }

  /* ---------------- Drawers ----------------
     Two drawers share this code: the cart and, below 900px, the menu.
     aria-modal only tells the truth if focus is actually trapped, so Tab
     is wrapped inside whichever drawer is open. */
  var cartDrawer = document.querySelector('[data-drawer="cart"]');
  var openEl = null;
  var lastFocus = null;

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  function openDrawer(name) {
    var el = name ? document.querySelector('[data-drawer="' + name + '"]') : cartDrawer;
    if (!el) return;
    lastFocus = document.activeElement;
    openEl = el;
    el.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var close = el.querySelector('[data-drawer-close]');
    if (close) close.focus();
    document.addEventListener('keydown', onKeydown);
  }

  function closeDrawer() {
    if (!openEl) return;
    openEl.classList.remove('is-open');
    openEl = null;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab' || !openEl) return;

    var items = focusables(openEl);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* Cart page is server rendered, so mutating the cart has to re-render it. */
  function refreshCartSection() {
    var host = document.querySelector('[data-cart-section]');
    if (!host) return;

    // Render the section in the context of the page it lives on, preserving
    // any existing query string.
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', host.getAttribute('data-cart-section'));

    fetch(url.toString())
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-section]');
        if (next) host.innerHTML = next.innerHTML;
        else window.location.reload();
      })
      .catch(function () { window.location.reload(); });
  }

  /* ---------------- Renderers ---------------- */
  function renderCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
  }

  function renderShipping(cart) {
    var threshold = cfg.freeShippingThreshold || 0;
    document.querySelectorAll('[data-ship]').forEach(function (el) {
      if (!threshold) { el.hidden = true; return; }
      el.hidden = false;
      var remaining = threshold - cart.total_price;
      var text = el.querySelector('[data-ship-text]');
      var fill = el.querySelector('[data-ship-fill]');
      if (remaining > 0) {
        text.innerHTML = (cfg.strings.away || 'Add {{ amount }} more for free delivery')
          .replace('{{ amount }}', '<strong>' + money(remaining) + '</strong>');
      } else {
        text.innerHTML = cfg.strings.unlocked || 'Free delivery unlocked';
      }
      var pct = Math.min(100, (cart.total_price / threshold) * 100);
      fill.style.width = pct + '%';
    });
  }

  function renderBundle(cart) {
    var size = cfg.bundleSize || 3;
    var bar = document.querySelector('[data-bundle]');
    if (!bar) return;
    if (!cfg.bundlePrice) { bar.hidden = true; return; }

    var slots = bar.querySelectorAll('[data-bundle-slot]');
    var images = [];
    cart.items.forEach(function (item) {
      for (var i = 0; i < item.quantity; i++) images.push(item.image);
    });

    slots.forEach(function (slot, i) {
      slot.innerHTML = '';
      if (images[i]) {
        var img = document.createElement('img');
        img.src = images[i].replace(/(\.[a-z]+)(\?|$)/i, '_120x120$1$2');
        img.alt = '';
        slot.appendChild(img);
        slot.classList.add('bundlebar__slot--filled');
      } else {
        slot.classList.remove('bundlebar__slot--filled');
        slot.textContent = i + 1;
      }
    });

    var copy = bar.querySelector('[data-bundle-copy]');
    var left = size - cart.item_count;
    if (left > 0) {
      copy.innerHTML = (cfg.strings.bundleProgress || 'Add <strong>{{ count }}</strong> more, pay {{ price }}')
        .replace('{{ count }}', '<strong>' + left + '</strong>')
        .replace('{{ price }}', money(cfg.bundlePrice));
    } else {
      copy.innerHTML = cfg.strings.bundleComplete || 'Bundle price applied at checkout';
    }
  }

  function renderDrawerItems(cart) {
    var body = cartDrawer && cartDrawer.querySelector('[data-drawer-items]');
    if (!body) return;

    if (!cart.items.length) {
      body.innerHTML = '<p class="empty">Your bag is empty.</p>';
    } else {
      body.innerHTML = cart.items.map(function (item) {
        var img = item.image
          ? '<img src="' + esc(item.image.replace(/(\.[a-z]+)(\?|$)/i, '_128x128$1$2')) + '" alt="" width="64" height="64">'
          : '<div style="width:64px;height:64px;border-radius:10px;background:var(--line)"></div>';
        var variant = item.variant_title && item.variant_title.indexOf('Default') === -1
          ? '<div style="font-size:var(--step--1);color:var(--ink-soft)">' + esc(item.variant_title) + '</div>'
          : '';
        return '<div class="lineitem">' + img +
          '<div><a class="lineitem__title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
          variant +
          '<div class="qty" role="group" aria-label="Quantity">' +
          '<button type="button" data-qty="' + esc(item.key) + '" data-to="' + (item.quantity - 1) + '" aria-label="Remove one">&minus;</button>' +
          '<span>' + item.quantity + '</span>' +
          '<button type="button" data-qty="' + esc(item.key) + '" data-to="' + (item.quantity + 1) + '" aria-label="Add one">+</button>' +
          '</div></div>' +
          '<div class="card__price">' + esc(money(item.final_line_price)) + '</div></div>';
      }).join('');
    }

    var total = cartDrawer.querySelector('[data-drawer-total]');
    if (total) total.textContent = money(cart.total_price);
    var checkout = cartDrawer.querySelector('[data-checkout]');
    if (checkout) checkout.disabled = cart.items.length === 0;
  }

  /* ---------------- Events ---------------- */
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var openBtn = e.target.closest('[data-drawer-open]');
    if (openBtn) {
      e.preventDefault();
      openDrawer(openBtn.getAttribute('data-drawer-open') || 'cart');
      return;
    }

    if (e.target.closest('[data-drawer-close]') || e.target.closest('.drawer__scrim')) {
      e.preventDefault(); closeDrawer(); return;
    }

    var add = e.target.closest('[data-add]');
    if (add) {
      e.preventDefault();
      var id = add.getAttribute('data-add');
      var original = add.textContent;
      add.disabled = true;
      addItem(id, 1)
        .then(function () {
          add.setAttribute('data-state', 'added');
          add.textContent = 'Added';
          openDrawer('cart');
          setTimeout(function () {
            add.removeAttribute('data-state');
            add.textContent = original;
            add.disabled = false;
          }, 1400);
        })
        .catch(function (err) {
          add.textContent = err.message;
          add.disabled = false;
        });
      return;
    }

    var qty = e.target.closest('[data-qty]');
    if (qty) {
      e.preventDefault();
      qty.disabled = true;
      changeItem(qty.getAttribute('data-qty'), parseInt(qty.getAttribute('data-to'), 10))
        .then(refreshCartSection)
        .catch(function () { window.location.reload(); });
    }
  });

  subscribe(renderCount);
  subscribe(renderShipping);
  subscribe(renderBundle);
  subscribe(renderDrawerItems);

  fetchCart();

  window.theme.cart = { fetch: fetchCart, add: addItem, change: changeItem, subscribe: subscribe, money: money };
  window.theme.openDrawer = openDrawer;
  window.theme.closeDrawer = closeDrawer;
})();
